import { useEffect, useState } from 'react';
import { Printer, RotateCcw, Rows3, StretchVertical, SlidersHorizontal, ChevronsRight } from 'lucide-react';

/**
 * Panneau flottant « Exporter en PDF » + réglages de mise en page.
 * Masqué en @media print via CSS (.ep-print-bar).
 *
 * Repliable : ouvert par défaut sur grand écran, REPLIÉ par défaut quand le
 * panneau chevaucherait la feuille A4 centrée (≤ 1340px, cf. eval-print.css).
 * Replié = deux boutons ronds (export, réglages) ; la notice de layout reste
 * visible dans les deux états (c'est un retour du moteur, jamais masqué).
 *
 * Props optionnelles (réglages de mise en page) :
 *   - reponseMode / onGlobalReponse : axe FORMAT « Réponses : QCM / Libres ».
 *     Libres = les QCM à choix textuels deviennent des questions à réponse
 *     libre (choix masqués, lignes de réponse) ; les contrôles par carte
 *     permettent de revenir au QCM sur un exercice précis ;
 *   - mode / onToggleMode : commutateur de densité « Confort / Compact ».
 *     Confort = rendu historique (DÉFAUT — préservé pour les élèves à
 *     difficultés de lecture) ; Compact = densité maximale.
 *   - onGlobalZoom / onGlobalHalf / onGlobalWidthAuto : réglages appliqués à
 *     TOUS les exercices d'un coup (les contrôles par carte restent dispo
 *     pour affiner ensuite — même mécanique, écriture en masse) ;
 *   - showReset / onReset : bouton « Tout réinitialiser » quand des réglages
 *     prof (zoom, largeur, mode, format) sont actifs ;
 *   - notice : message de layout (ex. rollback après non-convergence).
 */
const MQ_ETROIT = '(max-width: 1340px)';

const estEtroit = () => (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(MQ_ETROIT).matches
        : false
);

const PrintButton = ({
    onReset, showReset = false, notice = null, mode = null, onToggleMode = null,
    onGlobalZoom = null, onGlobalHalf = null, onGlobalWidthAuto = null,
    reponseMode = null, onGlobalReponse = null,
}) => {
    const [open, setOpen] = useState(() => !estEtroit());

    // Suit les changements de largeur (rotation, redimensionnement) : on
    // replie en passant étroit, on déplie en repassant large.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mq = window.matchMedia(MQ_ETROIT);
        const onChange = (e) => setOpen(!e.matches);
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);

    const handlePrint = () => {
        window.print();
    };

    if (!open) {
        return (
            <div className="ep-print-bar ep-print-bar-collapsed">
                <div className="ep-print-fabs">
                    <button
                        type="button"
                        className="ep-print-fab ep-print-fab-primary"
                        title="Exporter en PDF"
                        aria-label="Exporter en PDF"
                        onClick={handlePrint}
                    >
                        <Printer size={18} />
                    </button>
                    <button
                        type="button"
                        className="ep-print-fab"
                        title="Ouvrir les réglages de mise en page"
                        aria-label="Ouvrir les réglages de mise en page"
                        onClick={() => setOpen(true)}
                    >
                        <SlidersHorizontal size={18} />
                    </button>
                </div>
                {notice ? <div className="ep-layout-notice">{notice}</div> : null}
            </div>
        );
    }

    return (
        <div className="ep-print-bar">
            <button
                type="button"
                className="ep-print-hide"
                title="Replier le panneau"
                onClick={() => setOpen(false)}
            >
                Replier
                <ChevronsRight size={14} />
            </button>
            <button type="button" className="ep-print-btn" onClick={handlePrint}>
                <Printer size={18} />
                Exporter en PDF
            </button>
            <div className="ep-print-hint">
                Dans le dialogue d'impression, choisis <strong>« Enregistrer au format PDF »</strong>.
            </div>
            {reponseMode && onGlobalReponse ? (
                <div className="ep-globals-row">
                    <span className="ep-globals-label">Réponses :</span>
                    <button
                        type="button"
                        className={`ep-global-btn${reponseMode === 'qcm' ? ' ep-global-btn-active' : ''}`}
                        title="Les QCM gardent leurs choix et les tableaux leurs cases (rendu de la banque)"
                        onClick={() => onGlobalReponse('qcm')}
                    >
                        À cocher
                    </button>
                    <button
                        type="button"
                        className={`ep-global-btn${reponseMode === 'libre' ? ' ep-global-btn-active' : ''}`}
                        title="Les QCM deviennent des questions à réponse libre (choix masqués, lignes de réponse) et les tableaux des listes où l'élève écrit chaque réponse (un Vrai/Faux demande une justification). QCM à images et tableaux d'association gardent leur support. Le corrigé donne la réponse en clair."
                        onClick={() => onGlobalReponse('libre')}
                    >
                        Libres
                    </button>
                </div>
            ) : null}
            {mode && onToggleMode ? (
                <button
                    type="button"
                    className="ep-reset-btn"
                    onClick={onToggleMode}
                    title={mode === 'compact'
                        ? 'Revenir à la mise en page aérée (recommandée pour les élèves à difficultés de lecture)'
                        : 'Densifier la mise en page (plus d\'exercices par page)'}
                >
                    {mode === 'compact'
                        ? <StretchVertical size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                        : <Rows3 size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />}
                    {mode === 'compact' ? 'Mode : Compact → repasser en Confort' : 'Mode : Confort → passer en Compact'}
                </button>
            ) : null}
            {onGlobalZoom ? (
                <div className="ep-globals-row">
                    <span className="ep-globals-label">Tous les exercices :</span>
                    <button type="button" className="ep-global-btn" title="Taille normale partout" onClick={() => onGlobalZoom(1)}>100 %</button>
                    <button type="button" className="ep-global-btn" title="Réduire tous les exercices à 85 %" onClick={() => onGlobalZoom(0.85)}>85 %</button>
                    <button type="button" className="ep-global-btn" title="Réduire tous les exercices à 70 %" onClick={() => onGlobalZoom(0.7)}>70 %</button>
                    <span className="ep-globals-sep">·</span>
                    <button
                        type="button"
                        className="ep-global-btn"
                        title="Forcer la demi-colonne partout (les exercices de tracé — constructions, placer des points — sont épargnés ; les trop larges seront refusés proprement)"
                        onClick={onGlobalHalf}
                    >
                        ½ col.
                    </button>
                    <button type="button" className="ep-global-btn" title="Laisser le moteur choisir les largeurs" onClick={onGlobalWidthAuto}>auto</button>
                </div>
            ) : null}
            {showReset && onReset ? (
                <button type="button" className="ep-reset-btn" onClick={onReset}>
                    <RotateCcw size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                    Tout réinitialiser
                </button>
            ) : null}
            {notice ? <div className="ep-layout-notice">{notice}</div> : null}
        </div>
    );
};

export default PrintButton;
